// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, ChevronDown, ChevronUp } from 'lucide-react';

const COOKIE_KEY = 'sapiens_cookie_consent';
const COOKIE_EXPIRY_DAYS = 180;

function getStoredConsent() {
  try {
    const raw = localStorage.getItem(COOKIE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.expiry || Date.now() > parsed.expiry) {
      localStorage.removeItem(COOKIE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function storeConsent(analytics, marketing) {
  const expiry = Date.now() + COOKIE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
  localStorage.setItem(COOKIE_KEY, JSON.stringify({ analytics, marketing, expiry, ts: new Date().toISOString() }));
}

function loadGA4() {
  if (window._ga4Loaded) return;
  window._ga4Loaded = true;
  const GA_ID = 'G-CZF9C8RRF2';
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  script.async = true;
  document.head.appendChild(script);
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', GA_ID, { anonymize_ip: true });
}

// Deployment 30 — Runtime opt-in/out per PostHog auto-injected dalla piattaforma.
// L'init in index.html chiama opt_out_capturing() al primo caricamento se il consenso
// analytics manca. Quando l'utente cambia preferenze qui, sincronizziamo lo stato.
function syncPostHogConsent(hasAnalyticsConsent) {
  try {
    if (typeof window === 'undefined' || !window.posthog) return;
    if (hasAnalyticsConsent) {
      if (typeof window.posthog.opt_in_capturing === 'function') {
        window.posthog.opt_in_capturing();
      }
    } else if (typeof window.posthog.opt_out_capturing === 'function') {
      window.posthog.opt_out_capturing();
    }
  } catch {
    // no-op: PostHog non ancora caricato o snippet incompatibile
  }
}

const CookieBanner = () => {
  const [consent, setConsent] = useState(getStoredConsent);
  const [showBanner, setShowBanner] = useState(false);
  const [showPersonalize, setShowPersonalize] = useState(false);
  const [analyticsOpt, setAnalyticsOpt] = useState(false);
  const [marketingOpt, setMarketingOpt] = useState(false);
  const [showFab, setShowFab] = useState(false);

  useEffect(() => {
    if (!consent) {
      const timer = setTimeout(() => setShowBanner(true), 800);
      return () => clearTimeout(timer);
    } else {
      setShowFab(true);
      if (consent.analytics) loadGA4();
      // Deployment 30 — sincronizza lo stato PostHog al mount con il consenso salvato
      syncPostHogConsent(consent.analytics === true);
    }
  }, [consent]);

  // Deployment 23 — Sezione C: dismiss-on-scroll.
  // Quando l'utente scrolla oltre 100px e non ha ancora accettato/rifiutato,
  // il banner viene nascosto e il FAB mostrato. Al refresh il banner riappare
  // (perché `consent` resta null finché l'utente non interagisce).
  useEffect(() => {
    if (consent) return; // utente ha già scelto: nessuna logica scroll
    const onScroll = () => {
      if (window.scrollY > 100 && showBanner) {
        setShowBanner(false);
        setShowFab(true);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [consent, showBanner]);

  const handleAcceptAll = () => {
    storeConsent(true, true);
    setConsent({ analytics: true, marketing: true });
    setShowBanner(false);
    setShowFab(true);
    loadGA4();
    syncPostHogConsent(true);
  };

  const handleRejectAll = () => {
    storeConsent(false, false);
    setConsent({ analytics: false, marketing: false });
    setShowBanner(false);
    setShowFab(true);
    syncPostHogConsent(false);
  };

  const handleSavePersonalize = () => {
    storeConsent(analyticsOpt, marketingOpt);
    setConsent({ analytics: analyticsOpt, marketing: marketingOpt });
    setShowBanner(false);
    setShowPersonalize(false);
    setShowFab(true);
    if (analyticsOpt) loadGA4();
    syncPostHogConsent(analyticsOpt);
  };

  const handleFabClick = () => {
    setShowFab(false);
    setShowBanner(true);
    if (consent) {
      setAnalyticsOpt(consent.analytics);
      setMarketingOpt(consent.marketing);
    }
  };

  return (
    <>
      {/* Floating cookie icon */}
      <AnimatePresence>
        {showFab && !showBanner && (
          <motion.button
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            onClick={handleFabClick}
            data-testid="cookie-fab"
            style={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 9998,
              background: '#252523',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '50%',
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
            }}
            title="Gestisci preferenze cookie"
            aria-label="Gestisci preferenze cookie"
          >
            <Cookie className="w-5 h-5 text-[#C9A84C]" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Cookie Banner */}
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 60 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            data-testid="cookie-banner"
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 9999,
              background: '#1A1A18',
              borderTop: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 -8px 40px rgba(0,0,0,0.5)',
              padding: '20px 24px',
            }}
          >
            <div style={{ maxWidth: 900, margin: '0 auto' }}>
              {/* Personalizza panel */}
              <AnimatePresence>
                {showPersonalize && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    style={{ overflow: 'hidden', marginBottom: 16 }}
                  >
                    <div style={{ background: '#252523', borderRadius: 10, padding: '16px', marginBottom: 8 }}>
                      <p style={{ color: '#C9A84C', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Personalizza preferenze</p>
                      <div className="space-y-3">
                        {/* Strictly necessary - always on */}
                        <div className="flex items-center justify-between">
                          <div>
                            <p style={{ color: '#E8E8E4', fontSize: 13, fontWeight: 500 }}>Cookie tecnici</p>
                            <p style={{ color: '#888780', fontSize: 11 }}>Necessari per il funzionamento del sito. Non disattivabili.</p>
                          </div>
                          <div style={{ background: '#3D7A52', borderRadius: 20, padding: '3px 10px', fontSize: 11, color: '#E8E8E4', fontWeight: 600 }}>Sempre attivo</div>
                        </div>
                        {/* Analytics */}
                        <div className="flex items-center justify-between">
                          <div>
                            <p style={{ color: '#E8E8E4', fontSize: 13, fontWeight: 500 }}>Cookie analitici (GA4)</p>
                            <p style={{ color: '#888780', fontSize: 11 }}>Statistiche anonime su utilizzo del sito (Google Analytics 4).</p>
                          </div>
                          <button
                            onClick={() => setAnalyticsOpt(v => !v)}
                            data-testid="analytics-toggle"
                            style={{
                              width: 44, height: 24, borderRadius: 12,
                              background: analyticsOpt ? '#3D7A52' : '#444442',
                              border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s',
                            }}
                          >
                            <span style={{
                              position: 'absolute', top: 3, borderRadius: '50%',
                              width: 18, height: 18, background: '#fff', transition: 'left 0.2s',
                              left: analyticsOpt ? 23 : 3,
                            }} />
                          </button>
                        </div>
                        {/* Marketing */}
                        <div className="flex items-center justify-between">
                          <div>
                            <p style={{ color: '#E8E8E4', fontSize: 13, fontWeight: 500 }}>Cookie di profilazione</p>
                            <p style={{ color: '#888780', fontSize: 11 }}>Pubblicità personalizzata (attualmente non in uso).</p>
                          </div>
                          <button
                            onClick={() => setMarketingOpt(v => !v)}
                            data-testid="marketing-toggle"
                            style={{
                              width: 44, height: 24, borderRadius: 12,
                              background: marketingOpt ? '#3D7A52' : '#444442',
                              border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s',
                            }}
                          >
                            <span style={{
                              position: 'absolute', top: 3, borderRadius: '50%',
                              width: 18, height: 18, background: '#fff', transition: 'left 0.2s',
                              left: marketingOpt ? 23 : 3,
                            }} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 280 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <Cookie className="w-4 h-4 text-[#C9A84C]" />
                    <p style={{ color: '#E8E8E4', fontSize: 14, fontWeight: 600 }}>Informativa cookie</p>
                  </div>
                  <p style={{ color: '#888780', fontSize: 12, lineHeight: 1.5 }}>
                    Usiamo cookie tecnici (necessari) e, con il tuo consenso, cookie analitici (GA4) per migliorare il sito.
                    Puoi modificare le preferenze in qualsiasi momento.{' '}
                    <a href="/cookie-policy" style={{ color: '#C9A84C', textDecoration: 'underline' }}>Cookie Policy</a>
                    {' · '}
                    <a href="/privacy" style={{ color: '#C9A84C', textDecoration: 'underline' }}>Privacy Policy</a>
                  </p>
                </div>

                {/* 4 equal-weight buttons — Provv. Garante 8 gennaio 2022 */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  <button
                    onClick={handleAcceptAll}
                    data-testid="cookie-accept-all"
                    style={{
                      padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                      background: '#252523', color: '#E8E8E4', border: '1px solid rgba(255,255,255,0.12)', cursor: 'pointer',
                      whiteSpace: 'nowrap', transition: 'opacity 0.2s',
                    }}
                  >
                    Accetta tutto
                  </button>
                  <button
                    onClick={handleRejectAll}
                    data-testid="cookie-reject-all"
                    style={{
                      padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                      background: '#252523', color: '#E8E8E4', border: '1px solid rgba(255,255,255,0.12)', cursor: 'pointer',
                      whiteSpace: 'nowrap', transition: 'opacity 0.2s',
                    }}
                  >
                    Rifiuta tutto
                  </button>
                  <button
                    onClick={() => {
                      setShowPersonalize(v => !v);
                      if (!showPersonalize && consent) {
                        setAnalyticsOpt(consent.analytics);
                        setMarketingOpt(consent.marketing);
                      }
                    }}
                    data-testid="cookie-personalize"
                    style={{
                      padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                      background: '#252523', color: '#E8E8E4', border: '1px solid rgba(255,255,255,0.12)', cursor: 'pointer',
                      whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4, transition: 'opacity 0.2s',
                    }}
                  >
                    Personalizza
                    {showPersonalize ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
                  </button>
                  {showPersonalize && (
                    <button
                      onClick={handleSavePersonalize}
                      data-testid="cookie-save-preferences"
                      style={{
                        padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                        background: '#252523', color: '#E8E8E4', border: '1px solid rgba(255,255,255,0.12)', cursor: 'pointer',
                        whiteSpace: 'nowrap', transition: 'opacity 0.2s',
                      }}
                    >
                      Salva preferenze
                    </button>
                  )}
                  <button
                    onClick={handleRejectAll}
                    data-testid="cookie-close-x"
                    style={{
                      padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                      background: '#252523', color: '#E8E8E4', border: '1px solid rgba(255,255,255,0.12)', cursor: 'pointer',
                      whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'color 0.2s',
                    }}
                    title="Chiudi (solo tecnici)"
                    aria-label="Chiudi banner"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CookieBanner;
